/**
 * Theme constants for The Watchtower
 * Consistent color scheme across all pages
 */

// Party configuration with colors
export const PARTY_CONFIG = {
  labour: { 
    color: '#DC2626', 
    gradient: 'from-red-600 to-red-500',
    bg: 'bg-red-500',
    hover: 'hover:bg-red-600',
    text: 'text-red-600',
    border: 'border-red-500',
    label: 'Labour',
    lightBg: 'bg-red-50 hover:bg-red-100'
  },
  conservative: { 
    color: '#2563EB', 
    gradient: 'from-blue-600 to-blue-500',
    bg: 'bg-blue-600',
    hover: 'hover:bg-blue-700',
    text: 'text-blue-600',
    border: 'border-blue-600',
    label: 'Conservative',
    lightBg: 'bg-blue-50 hover:bg-blue-100'
  },
  libdem: { 
    color: '#F97316', 
    gradient: 'from-orange-500 to-amber-500',
    bg: 'bg-orange-500',
    hover: 'hover:bg-orange-600',
    text: 'text-orange-500',
    border: 'border-orange-500',
    label: 'Lib Dem',
    lightBg: 'bg-orange-50 hover:bg-orange-100'
  },
  snp: { 
    color: '#EAB308', 
    gradient: 'from-yellow-500 to-yellow-400',
    bg: 'bg-yellow-500',
    hover: 'hover:bg-yellow-600',
    text: 'text-yellow-600',
    border: 'border-yellow-500',
    label: 'SNP',
    lightBg: 'bg-yellow-50 hover:bg-yellow-100'
  },
  reform: { 
    color: '#06B6D4', 
    gradient: 'from-cyan-500 to-cyan-400',
    bg: 'bg-cyan-500',
    hover: 'hover:bg-cyan-600',
    text: 'text-cyan-600',
    border: 'border-cyan-500',
    label: 'Reform UK',
    lightBg: 'bg-cyan-50 hover:bg-cyan-100'
  },
  green: { 
    color: '#22C55E', 
    gradient: 'from-green-500 to-emerald-500',
    bg: 'bg-green-500',
    hover: 'hover:bg-green-600',
    text: 'text-green-600',
    border: 'border-green-500',
    label: 'Green',
    lightBg: 'bg-green-50 hover:bg-green-100'
  },
  plaid: { 
    color: '#059669', 
    gradient: 'from-emerald-600 to-emerald-500',
    bg: 'bg-emerald-600',
    hover: 'hover:bg-emerald-700',
    text: 'text-emerald-600',
    border: 'border-emerald-600',
    label: 'Plaid Cymru',
    lightBg: 'bg-emerald-50 hover:bg-emerald-100'
  },
  dup: { 
    color: '#991B1B', 
    gradient: 'from-red-800 to-red-700',
    bg: 'bg-red-800',
    hover: 'hover:bg-red-900',
    text: 'text-red-800',
    border: 'border-red-800',
    label: 'DUP',
    lightBg: 'bg-red-50 hover:bg-red-100'
  },
  sinnfein: { 
    color: '#15803D', 
    gradient: 'from-green-700 to-green-600',
    bg: 'bg-green-700',
    hover: 'hover:bg-green-800',
    text: 'text-green-700',
    border: 'border-green-700',
    label: 'Sinn Féin',
    lightBg: 'bg-green-50 hover:bg-green-100'
  },
  independent: {
    color: '#6B7280',
    gradient: 'from-gray-500 to-gray-400',
    bg: 'bg-gray-500',
    hover: 'hover:bg-gray-600',
    text: 'text-gray-600',
    border: 'border-gray-500',
    label: 'Independent',
    lightBg: 'bg-gray-50 hover:bg-gray-100'
  },
  other: {
    color: '#9CA3AF',
    gradient: 'from-gray-400 to-gray-300',
    bg: 'bg-gray-400',
    hover: 'hover:bg-gray-500',
    text: 'text-gray-500',
    border: 'border-gray-400',
    label: 'Other',
    lightBg: 'bg-gray-50 hover:bg-gray-100'
  }
};

// Get party config with fallback
export const getPartyConfig = (party) => {
  return PARTY_CONFIG[party?.toLowerCase()] || PARTY_CONFIG.other;
};

// Theme gradients for sections
export const SECTION_GRADIENTS = {
  hero: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
  primary: 'bg-gradient-to-br from-blue-600 to-purple-600',
  secondary: 'bg-gradient-to-br from-slate-700 to-slate-600',
  accent: 'bg-gradient-to-br from-orange-500 to-red-500',
  success: 'bg-gradient-to-br from-green-500 to-emerald-500',
  warning: 'bg-gradient-to-br from-amber-500 to-orange-500',
};

// Card styles
export const CARD_STYLES = {
  default: 'bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all',
  elevated: 'bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all',
  gradient: 'bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200',
};

// Button variants
export const BUTTON_VARIANTS = {
  primary: 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all',
  secondary: 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium rounded-xl transition-all',
  danger: 'bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white font-bold rounded-xl shadow-lg transition-all',
};

// Animation classes
export const ANIMATIONS = {
  fadeIn: 'animate-fadeIn',
  slideUp: 'animate-slideUp',
  pulse: 'animate-pulse',
  bounce: 'animate-bounce',
};

// Chart colors array for recharts
export const CHART_COLORS = [
  '#DC2626', // Labour
  '#2563EB', // Conservative
  '#F97316', // Lib Dem
  '#06B6D4', // Reform
  '#22C55E', // Green
  '#EAB308', // SNP
  '#059669', // Plaid
  '#991B1B', // DUP
  '#15803D', // Sinn Féin
  '#6B7280', // Other
];

// 2024 Election baseline for seat calculations
export const BASELINE_2024 = {
  labour: { votes: 33.7, seats: 411 },
  conservative: { votes: 23.7, seats: 121 },
  libdem: { votes: 12.2, seats: 72 },
  reform: { votes: 14.3, seats: 5 },
  green: { votes: 6.8, seats: 4 },
  snp: { votes: 2.5, seats: 9 },
  plaid: { votes: 0.7, seats: 4 },
  other: { votes: 6.1, seats: 24 }
};
